import React, { useEffect, useState } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { 
  Plus, Calendar as CalendarIcon, Clock, Bell, CheckCircle2, Circle,
  Loader2, Trash2, Edit, MoreVertical, User, Users, AlertTriangle,
  ChevronDown, Filter, ListTodo, CheckSquare, Square, Type, FileText, Flag
} from 'lucide-react';
import { format, parseISO, isValid } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { PageHeader } from '../components/common/PageHeader';

const priorityConfig = {
  low: { label: 'Baixa', color: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300' },
  medium: { label: 'Média', color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' },
  high: { label: 'Alta', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
};

const reminderOptions = [
  { value: 0, label: 'No dia' },
  { value: 1, label: '1 dia antes' },
  { value: 2, label: '2 dias antes' },
  { value: 3, label: '3 dias antes' },
  { value: 5, label: '5 dias antes' },
  { value: 7, label: '1 semana antes' },
];

export default function TripTasks() {
  const { tripId } = useParams();
  const context = useOutletContext();
  const { trip, isOrganizer, members } = context || {};
  const { api, user } = useAuth();
  
  const [tasks, setTasks] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    due_date: null,
    remind_before_days: null,
    assigned_to: '',
    is_public: false,
    priority: 'medium',
  });

  useEffect(() => {
    if (tripId) {
      fetchTasks();
      fetchSummary();
    }
  }, [tripId, filter]);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/trips/${tripId}/tasks`, {
        params: { filter: filter !== 'all' ? filter : undefined }
      });
      setTasks(response.data);
    } catch (error) {
      console.error('Error fetching tasks:', error);
      toast.error('Erro ao carregar lembretes');
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const response = await api.get(`/trips/${tripId}/tasks/summary`);
      setSummary(response.data);
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      due_date: null,
      remind_before_days: null,
      assigned_to: '',
      is_public: false,
      priority: 'medium',
    });
    setEditingTask(null);
  };

  const handleOpenDialog = (task = null) => {
    if (task) {
      setEditingTask(task);
      setFormData({
        title: task.title,
        description: task.description || '',
        due_date: task.due_date ? parseISO(task.due_date) : null,
        remind_before_days: task.remind_before_days,
        assigned_to: task.assigned_to || '',
        is_public: task.is_public,
        priority: task.priority,
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      toast.error('Digite um título para o lembrete');
      return;
    }
    
    try {
      setSubmitting(true);
      
      const payload = {
        title: formData.title.trim(),
        description: formData.description?.trim() || null,
        due_date: formData.due_date ? formData.due_date.toISOString() : null,
        remind_before_days: formData.remind_before_days,
        assigned_to: formData.assigned_to || null,
        is_public: formData.is_public,
        priority: formData.priority,
      };
      
      if (editingTask) {
        await api.put(`/trips/${tripId}/tasks/${editingTask.id}`, payload);
        toast.success('Lembrete atualizado!');
      } else {
        await api.post(`/trips/${tripId}/tasks`, payload);
        toast.success('Lembrete criado!');
      }
      
      setDialogOpen(false);
      resetForm();
      fetchTasks();
      fetchSummary();
    } catch (error) {
      console.error('Error saving task:', error);
      toast.error(error.response?.data?.detail || 'Erro ao salvar lembrete');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleComplete = async (task) => {
    try {
      await api.put(`/trips/${tripId}/tasks/${task.id}/toggle`);
      toast.success(task.is_completed ? 'Lembrete reaberto' : 'Lembrete concluído!');
      fetchTasks();
      fetchSummary();
    } catch (error) {
      console.error('Error toggling task:', error);
      toast.error(error.response?.data?.detail || 'Erro ao atualizar lembrete');
    }
  };

  const handleDelete = async () => {
    if (!taskToDelete) return;
    
    try {
      await api.delete(`/trips/${tripId}/tasks/${taskToDelete.id}`);
      toast.success('Lembrete excluído');
      setDeleteDialogOpen(false);
      setTaskToDelete(null);
      fetchTasks();
      fetchSummary();
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error(error.response?.data?.detail || 'Erro ao excluir lembrete');
    }
  };

  const confirmedMembers = members?.filter(m => m.status === 'confirmed') || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title="Lembretes"
        subtitle="Crie e gerencie tarefas e lembretes da viagem"
        helpScreen="tasks"
        actions={
          <Button 
            className="rounded-full bg-orange-500 hover:bg-orange-600"
            onClick={() => handleOpenDialog()}
            data-testid="add-task-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Novo Lembrete
          </Button>
        }
      />

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Card className="border-slate-200 dark:border-slate-700">
            <CardContent className="p-4 text-center">
              <ListTodo className="w-5 h-5 mx-auto mb-1 text-slate-500" />
              <p className="text-2xl font-bold text-slate-900 dark:text-white">{summary.total}</p>
              <p className="text-xs text-slate-500">Total</p>
            </CardContent>
          </Card>
          <Card className="border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900/10">
            <CardContent className="p-4 text-center">
              <Circle className="w-5 h-5 mx-auto mb-1 text-yellow-500" />
              <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{summary.pending}</p>
              <p className="text-xs text-yellow-600 dark:text-yellow-400">Pendentes</p>
            </CardContent>
          </Card>
          <Card className="border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/10">
            <CardContent className="p-4 text-center">
              <CheckCircle2 className="w-5 h-5 mx-auto mb-1 text-green-500" />
              <p className="text-2xl font-bold text-green-600 dark:text-green-400">{summary.completed}</p>
              <p className="text-xs text-green-600 dark:text-green-400">Concluídos</p>
            </CardContent>
          </Card>
          <Card className="border-orange-200 dark:border-orange-800 bg-orange-50 dark:bg-orange-900/10">
            <CardContent className="p-4 text-center">
              <User className="w-5 h-5 mx-auto mb-1 text-orange-500" />
              <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">{summary.my_pending}</p>
              <p className="text-xs text-orange-600 dark:text-orange-400">Meus pendentes</p>
            </CardContent>
          </Card>
          {summary.overdue > 0 && (
            <Card className="border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/10">
              <CardContent className="p-4 text-center">
                <AlertTriangle className="w-5 h-5 mx-auto mb-1 text-red-500" />
                <p className="text-2xl font-bold text-red-600 dark:text-red-400">{summary.overdue}</p>
                <p className="text-xs text-red-600 dark:text-red-400">Atrasados</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Filters */}
      <Tabs value={filter} onValueChange={setFilter} className="w-full">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="all" data-testid="filter-all">Todos</TabsTrigger>
          <TabsTrigger value="pending" data-testid="filter-pending">Pendentes</TabsTrigger>
          <TabsTrigger value="completed" data-testid="filter-completed">Concluídos</TabsTrigger>
          <TabsTrigger value="assigned" data-testid="filter-assigned">Para mim</TabsTrigger>
          <TabsTrigger value="mine" data-testid="filter-mine">Criados por mim</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Tasks List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
        </div>
      ) : tasks.length === 0 ? (
        <Card className="border-slate-100 dark:border-slate-800 border-dashed">
          <CardContent className="py-12 text-center">
            <ListTodo className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="font-heading font-bold text-lg text-slate-900 dark:text-white mb-2">
              Nenhum lembrete
            </h3>
            <p className="text-slate-500 dark:text-slate-400 mb-4">
              {filter === 'all' 
                ? 'Crie lembretes para você ou para outros membros'
                : 'Nenhum lembrete encontrado com este filtro'}
            </p>
            {filter === 'all' && (
              <Button
                variant="outline"
                className="rounded-full"
                onClick={() => handleOpenDialog()}
              >
                <Plus className="w-4 h-4 mr-2" />
                Criar primeiro lembrete
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          <AnimatePresence>
            {tasks.map((task, index) => (
              <TaskCard
                key={task.id}
                task={task}
                index={index}
                currentUserId={user?.id}
                isOrganizer={isOrganizer}
                onToggle={() => handleToggleComplete(task)}
                onEdit={() => handleOpenDialog(task)}
                onDelete={() => {
                  setTaskToDelete(task);
                  setDeleteDialogOpen(true);
                }}
              />
            ))}
          </AnimatePresence>
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingTask ? 'Editar Lembrete' : 'Novo Lembrete'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-2">
            <div className="py-2">
              <label className="text-xs text-slate-500 dark:text-slate-400 block mb-1">Título *</label>
              <div className="flex items-center gap-3 border-b border-slate-200 dark:border-slate-700 focus-within:border-orange-500">
                <Type className="w-5 h-5 text-slate-400 flex-shrink-0" />
                <input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Ex: Comprar passagem, Tirar passaporte..."
                  className="w-full bg-transparent border-0 focus:ring-0 px-0 py-3 text-base placeholder:text-slate-400 text-slate-900 dark:text-white outline-none"
                  data-testid="task-title-input"
                />
              </div>
            </div>
            
            <div className="py-2">
              <label className="text-xs text-slate-500 dark:text-slate-400 block mb-1">Descrição</label>
              <div className="flex items-start gap-3 border-b border-slate-200 dark:border-slate-700 focus-within:border-orange-500">
                <FileText className="w-5 h-5 text-slate-400 flex-shrink-0 mt-3" />
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Detalhes adicionais..."
                  rows={2}
                  className="w-full bg-transparent border-0 focus:ring-0 px-0 py-3 text-base placeholder:text-slate-400 text-slate-900 dark:text-white resize-none outline-none"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="py-2">
                <label className="text-xs text-slate-500 dark:text-slate-400 block mb-1">Prazo</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className={cn(
                        "w-full text-left bg-transparent border-b border-slate-200 dark:border-slate-700 px-0 py-3 text-base focus:border-orange-500",
                        !formData.due_date && "text-slate-400"
                      )}
                    >
                      <div className="flex items-center gap-3">
                        <CalendarIcon className="w-5 h-5 text-slate-400 flex-shrink-0" />
                        {formData.due_date 
                          ? format(formData.due_date, "dd/MM/yyyy", { locale: ptBR })
                          : "Selecionar"}
                      </div>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={formData.due_date}
                      onSelect={(date) => setFormData({ ...formData, due_date: date })}
                      locale={ptBR}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              
              <div className="py-2">
                <label className="text-xs text-slate-500 dark:text-slate-400 block mb-1">Lembrar</label>
                <div className="flex items-center gap-3 border-b border-slate-200 dark:border-slate-700 focus-within:border-orange-500">
                  <Bell className="w-5 h-5 text-slate-400 flex-shrink-0" />
                  <Select
                    value={formData.remind_before_days?.toString() || 'none'}
                    onValueChange={(value) => setFormData({ 
                      ...formData, 
                      remind_before_days: value === 'none' ? null : parseInt(value) 
                    })}
                  >
                    <SelectTrigger className="w-full bg-transparent border-0 focus:ring-0 px-0 py-3 text-base h-auto rounded-none shadow-none">
                      <SelectValue placeholder="Não lembrar" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Não lembrar</SelectItem>
                      {reminderOptions.map(opt => (
                        <SelectItem key={opt.value} value={opt.value.toString()}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="py-2">
                <label className="text-xs text-slate-500 dark:text-slate-400 block mb-1">Prioridade</label>
                <div className="flex items-center gap-3 border-b border-slate-200 dark:border-slate-700 focus-within:border-orange-500">
                  <Flag className="w-5 h-5 text-slate-400 flex-shrink-0" />
                  <Select
                    value={formData.priority}
                    onValueChange={(value) => setFormData({ ...formData, priority: value })}
                  >
                    <SelectTrigger className="w-full bg-transparent border-0 focus:ring-0 px-0 py-3 text-base h-auto rounded-none shadow-none">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Baixa</SelectItem>
                      <SelectItem value="medium">Média</SelectItem>
                      <SelectItem value="high">Alta</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {isOrganizer && (
                <div className="py-2">
                  <label className="text-xs text-slate-500 dark:text-slate-400 block mb-1">Atribuir para</label>
                  <div className="flex items-center gap-3 border-b border-slate-200 dark:border-slate-700 focus-within:border-orange-500">
                    <User className="w-5 h-5 text-slate-400 flex-shrink-0" />
                    <Select
                      value={formData.assigned_to || 'myself'}
                      onValueChange={(value) => setFormData({ ...formData, assigned_to: value === 'myself' ? '' : value })}
                    >
                      <SelectTrigger className="w-full bg-transparent border-0 focus:ring-0 px-0 py-3 text-base h-auto rounded-none shadow-none">
                        <SelectValue placeholder="Para mim" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="myself">Para mim</SelectItem>
                        {confirmedMembers
                          .filter(m => m.user_id !== user?.id)
                          .map(member => (
                            <SelectItem key={member.user_id} value={member.user_id}>
                              {member.user_avatar} {member.user_name}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </div>
            
            <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg mt-4">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-slate-500" />
                <div>
                  <p className="text-sm font-medium text-slate-900 dark:text-white">
                    Visível para todos
                  </p>
                  <p className="text-xs text-slate-500">
                    Outros membros poderão ver este lembrete
                  </p>
                </div>
              </div>
              <Switch
                checked={formData.is_public}
                onCheckedChange={(checked) => setFormData({ ...formData, is_public: checked })}
              />
            </div>
            
            <div className="flex flex-col gap-3 pt-4 border-t border-slate-200 dark:border-slate-700 mt-4">
              <Button
                type="submit"
                className="w-full bg-orange-500 hover:bg-orange-600 rounded-2xl h-14 text-base font-semibold"
                disabled={submitting}
                data-testid="save-task-btn"
              >
                {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {editingTask ? 'Salvar' : 'Criar'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
                className="w-full rounded-2xl h-14 text-base font-semibold"
              >
                Cancelar
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Excluir lembrete?</DialogTitle>
            <DialogDescription>
              Esta ação não pode ser desfeita. O lembrete "{taskToDelete?.title}" será permanentemente excluído.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3 pt-4">
            <Button
              variant="destructive"
              onClick={handleDelete}
              className="w-full rounded-2xl h-14 text-base font-semibold"
              data-testid="confirm-delete-btn"
            >
              Excluir
            </Button>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              className="w-full rounded-2xl h-14 text-base font-semibold"
            >
              Cancelar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TaskCard({ task, index, currentUserId, isOrganizer, onToggle, onEdit, onDelete }) {
  const isAssignedToMe = task.assigned_to === currentUserId;
  const isMyTask = task.created_by === currentUserId;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ delay: index * 0.03 }}
    >
      <Card 
        className={cn(
          "border-slate-200 dark:border-slate-700 transition-all",
          task.is_completed && "opacity-60",
          task.is_overdue && !task.is_completed && "border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/10"
        )}
      >
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            {/* Checkbox */}
            <button
              onClick={onToggle}
              disabled={!task.can_complete}
              className={cn(
                "mt-0.5 flex-shrink-0 transition-colors",
                task.can_complete ? "cursor-pointer hover:text-orange-500" : "cursor-not-allowed opacity-50"
              )}
              data-testid={`toggle-task-${task.id}`}
            >
              {task.is_completed ? (
                <CheckCircle2 className="w-5 h-5 text-green-500" />
              ) : (
                <Circle className="w-5 h-5 text-slate-300 dark:text-slate-600" />
              )}
            </button>
            
            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1">
                  <h3 className={cn(
                    "font-medium text-slate-900 dark:text-white",
                    task.is_completed && "line-through text-slate-500"
                  )}>
                    {task.title}
                  </h3>
                  
                  {task.description && (
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 line-clamp-2">
                      {task.description}
                    </p>
                  )}
                  
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    {/* Due date */}
                    {task.due_date && (
                      <span className={cn(
                        "text-xs flex items-center gap-1",
                        task.is_overdue && !task.is_completed 
                          ? "text-red-600 dark:text-red-400 font-medium" 
                          : "text-slate-500"
                      )}>
                        <CalendarIcon className="w-3 h-3" />
                        {format(parseISO(task.due_date), "dd/MM", { locale: ptBR })}
                        {task.days_until_due !== null && !task.is_completed && (
                          <span>
                            ({task.days_until_due < 0 
                              ? `${Math.abs(task.days_until_due)}d atrasado` 
                              : task.days_until_due === 0 
                                ? 'hoje' 
                                : `${task.days_until_due}d`})
                          </span>
                        )}
                      </span>
                    )}
                    
                    {/* Reminder */}
                    {task.remind_before_days !== null && (
                      <span className="text-xs flex items-center gap-1 text-slate-500">
                        <Bell className="w-3 h-3" />
                        {task.remind_before_days === 0 
                          ? 'No dia' 
                          : `${task.remind_before_days}d antes`}
                      </span>
                    )}
                    
                    {/* Priority */}
                    <Badge className={cn("text-xs", priorityConfig[task.priority].color)}>
                      {priorityConfig[task.priority].label}
                    </Badge>
                    
                    {/* Public indicator */}
                    {task.is_public && (
                      <Badge variant="outline" className="text-xs">
                        <Users className="w-3 h-3 mr-1" />
                        Público
                      </Badge>
                    )}
                  </div>
                  
                  {/* Assigned to / Created by */}
                  <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
                    <span className="flex items-center gap-1">
                      <span>{task.assigned_to_avatar}</span>
                      {isAssignedToMe ? 'Para mim' : task.assigned_to_name}
                    </span>
                    {task.created_by !== task.assigned_to && (
                      <span className="text-slate-400">
                        por {isMyTask ? 'mim' : task.created_by_name}
                      </span>
                    )}
                    {task.is_completed && task.completed_by_name && (
                      <span className="text-green-600 dark:text-green-400">
                        ✓ Concluído por {task.completed_by === currentUserId ? 'mim' : task.completed_by_name}
                      </span>
                    )}
                  </div>
                </div>
                
                {/* Actions */}
                {(task.can_edit || task.can_delete) && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {task.can_edit && (
                        <DropdownMenuItem onClick={onEdit}>
                          <Edit className="w-4 h-4 mr-2" />
                          Editar
                        </DropdownMenuItem>
                      )}
                      {task.can_delete && (
                        <DropdownMenuItem 
                          onClick={onDelete}
                          className="text-red-600 focus:text-red-600"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Excluir
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}
